import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  RichTextContent,
  usePropertyField,
  useEditMode,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert an Instagram account URL (e.g. https://www.instagram.com/username/)
 * into the embeddable version (https://www.instagram.com/username/embed/).
 * Returns null if the URL doesn't look like a valid Instagram account URL.
 */
function toInstagramEmbedUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (!url.hostname.replace('www.', '').startsWith('instagram.com')) return null;
    // Normalise trailing slash and append /embed
    const path = url.pathname.replace(/\/+$/, '');
    if (!path || path === '/') return null;
    return `https://www.instagram.com${path}/embed`;
  } catch {
    return null;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InstagramSection() {
  const instagramUrl = usePropertyField('property_instagram_url') as string | null;
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const embedUrl = toInstagramEmbedUrl(instagramUrl);

  // Instagram's /embed endpoint posts a `MEASURE` message with the real
  // content height. We listen for it and grow the iframe to fit so the embed
  // takes its full natural height instead of scrolling inside a fixed box.
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [embedHeight, setEmbedHeight] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!embedUrl) return;

    function handleMessage(event: MessageEvent) {
      if (typeof event.origin !== 'string' || !event.origin.includes('instagram.com')) {
        return;
      }
      let data: unknown = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      const message = data as { type?: string; details?: { height?: number } } | null;
      if (
        message &&
        message.type === 'MEASURE' &&
        message.details &&
        typeof message.details.height === 'number' &&
        message.details.height > 0
      ) {
        setEmbedHeight(message.details.height);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [embedUrl]);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // No Instagram URL and not in edit mode → render nothing
  if (!instagramUrl && !isEditMode) {
    return null;
  }

  // No Instagram URL but in edit mode → show placeholder
  if (!instagramUrl && isEditMode) {
    return (
      <section className="py-16 md:py-24 bg-[var(--vibe-color-secondary)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-body text-base text-[var(--vibe-color-text-on-secondary)] opacity-70">
            Add an Instagram URL to your property to display this social block.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-secondary)]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        {/* Subtitle */}
        <RichTextContent
          path="instagramsection.subtitle"
          label="Subtitle"
          defaultValue="<p>Let's Be Friends</p>"
          as="p"
          className="font-body text-sm sm:text-base text-[var(--vibe-color-text-on-secondary)] mb-6 tracking-wide uppercase"
        />

        {/* Title */}
        <RichTextContent
          path="instagramsection.title"
          label="Title"
          defaultValue="<p>Follow Us On Social</p>"
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text-on-secondary)] mb-4 leading-tight uppercase"
        />

        {/* Accent divider */}
        <div className="w-20 h-1 mb-8 rounded-full bg-[var(--vibe-color-primary)]" />

        {/* Instagram embed */}
        <div className="mt-8">
          {embedUrl ? (
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title="Instagram Feed"
              width="100%"
              height={embedHeight ?? 600}
              scrolling="no"
              style={{
                border: 'none',
                display: 'block',
                width: '100%',
                height: embedHeight ? `${embedHeight}px` : '600px',
                overflow: 'hidden',
              }}
              allow="encrypted-media"
              allowFullScreen
              loading="lazy"
            />
          ) : isEditMode ? (
            <div className="flex items-center justify-center py-16 text-center rounded-lg bg-[var(--vibe-color-secondary)]">
              <p className="font-body text-sm text-[var(--vibe-color-text-on-secondary)] opacity-60">
                The Instagram URL doesn't appear to be a valid Instagram account link.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'InstagramSection',
  displayName: 'Instagram Section',
  tokens: {
    'instagramsection.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Let\'s Be Friends</p>',
    },
    'instagramsection.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Follow Us On Social</p>',
    },
  },
};

(InstagramSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InstagramSection;
